import { publishChange } from '@/lib/revalidation';

/**
 * Se o servidor está respondendo, e de quando é o dado que a tela mostra (Fase 43, RN-599).
 *
 * Quem escreve é o transporte (`offline-cache.ts`), que é o único que sabe se a resposta veio da
 * rede ou do aparelho. Quem lê é o indicador da barra. As telas não leem nada daqui: para elas
 * a resposta guardada é uma resposta como outra (ADR-192).
 */
export interface ConnectionSnapshot {
  readonly online: boolean;
  /**
   * O dado guardado mais antigo entregue desde que a conexão caiu. É o que o indicador mostra:
   * "dados de 08:12" diz que nada na tela é mais velho do que isso.
   */
  readonly oldestServedAt?: string;
}

/** Funções soltas, e não métodos: o `useSyncExternalStore` as recebe separadas do objeto. */
export interface ConnectionStatus {
  readonly snapshot: () => ConnectionSnapshot;
  readonly subscribe: (listener: () => void) => () => void;
  /** Houve resposta do servidor. A volta depois de uma queda refaz as leituras abertas. */
  readonly markOnline: () => void;
  /** Não houve resposta; `servedAt` é a hora do guardado entregue no lugar, quando houve. */
  readonly markOffline: (servedAt?: string) => void;
}

export function createConnectionStatus(onBack: () => void = publishChange): ConnectionStatus {
  let atual: ConnectionSnapshot = { online: true };
  const ouvintes = new Set<() => void>();

  const trocar = (novo: ConnectionSnapshot): void => {
    if (novo.online === atual.online && novo.oldestServedAt === atual.oldestServedAt) return;
    atual = novo;
    for (const ouvinte of [...ouvintes]) ouvinte();
  };

  return {
    snapshot: () => atual,
    subscribe: (listener) => {
      ouvintes.add(listener);
      return () => ouvintes.delete(listener);
    },
    markOnline: () => {
      const voltou = !atual.online;
      trocar({ online: true });
      // Depois do aviso: quem rebusca encontra o estado já de volta, e a resposta da rede
      // não é confundida com a queda que acabou.
      if (voltou) onBack();
    },
    markOffline: (servedAt) => {
      const anterior = atual.online ? undefined : atual.oldestServedAt;
      const maisAntigo =
        servedAt === undefined || (anterior !== undefined && anterior < servedAt)
          ? anterior
          : servedAt;
      trocar(
        maisAntigo === undefined
          ? { online: false }
          : { online: false, oldestServedAt: maisAntigo },
      );
    },
  };
}

/** De quanto em quanto tempo, sem conexão, as leituras abertas tentam a rede de novo. */
export const RETRY_EVERY_MS = 15_000;

/**
 * Sem conexão, tenta de novo a cada `everyMs` até a rede responder.
 *
 * O evento `online` do navegador não basta: ele só fala da placa de rede. O Wi-Fi da obra que
 * volta a ter internet, ou a barra de sinal que volta a passar dado, não dispara nada — e as
 * telas ficariam no guardado até a pessoa mexer em alguma coisa. Cada tentativa é a própria
 * leitura da tela (`retry`): a primeira que responder marca a volta, e a volta para o relógio.
 */
export function retryWhileOffline(
  status: ConnectionStatus,
  retry: () => void,
  everyMs: number = RETRY_EVERY_MS,
): () => void {
  let relogio: ReturnType<typeof setInterval> | undefined;
  const acompanhar = (): void => {
    const fora = !status.snapshot().online;
    if (fora && relogio === undefined) relogio = setInterval(retry, everyMs);
    if (!fora && relogio !== undefined) {
      clearInterval(relogio);
      relogio = undefined;
    }
  };
  const cancelar = status.subscribe(acompanhar);
  return () => {
    cancelar();
    if (relogio !== undefined) clearInterval(relogio);
  };
}

/** O da aplicação: um só, porque a conexão é do aparelho, e não de uma tela. */
export const connectionStatus = createConnectionStatus();

if (typeof window !== 'undefined') {
  retryWhileOffline(connectionStatus, publishChange);
  // Quando a placa de rede volta, o navegador avisa: não há por que esperar o relógio.
  window.addEventListener('online', () => {
    if (!connectionStatus.snapshot().online) publishChange();
  });
}
